import { SIGMA_DIFF, standardNormalCdf, calculateEqualStrengthDrawProbability } from "./chessProbability";

export const BETTY_STRENGTH_SCOPE = "tournament-section-initialization";

// Imported Elo 0 denotes an unrated player; missing/nonfinite ratings cannot anchor
// a population. The mathematical initializer below accepts any finite rating.
export function isValidTournamentElo(value) {
  return (typeof value === "number" || typeof value === "string") && value !== ""
    && Number.isFinite(Number(value)) && Number(value) > 0;
}

export function createTournamentStrengthReference(section, teams) {
  if (!["open", "women"].includes(section)) throw new Error("Unknown Olympiad section.");
  const registeredPlayers = teams.flatMap(team => team.roster || []);
  const ratings = registeredPlayers.map(player => player.rating_used).filter(isValidTournamentElo).map(Number);
  if (!ratings.length) return null;
  const sum = ratings.reduce((total, rating) => total + rating, 0);
  const referenceElo = Number.isFinite(sum) ? sum / ratings.length
    : ratings.reduce((mean, rating, index) => mean + (rating - mean) / (index + 1), 0);
  const lowestRatings = [...ratings].sort((a, b) => a - b).slice(0, 20);
  const replacementElo = lowestRatings.reduce((sum, rating) => sum + rating, 0) / lowestRatings.length;
  return Object.freeze({
    replacementElo,
    replacementPlayerCount: lowestRatings.length,
    scope: BETTY_STRENGTH_SCOPE,
    section,
    referenceElo,
    ratedPlayerCount: ratings.length,
    excludedPlayerCount: registeredPlayers.length - ratings.length,
  });
}

export function initializeBettyStrength(rating, reference) {
  if (typeof rating !== "number" || !Number.isFinite(rating)) throw new Error("A finite Elo is required.");
  if (reference?.scope !== BETTY_STRENGTH_SCOPE || !["open", "women"].includes(reference.section)
      || !Number.isFinite(reference.referenceElo)) throw new Error("A tournament-section reference is required.");
  return Object.freeze({
    scope: BETTY_STRENGTH_SCOPE,
    section: reference.section,
    referenceElo: reference.referenceElo,
    rating,
    strength: standardNormalCdf((rating - reference.referenceElo) / SIGMA_DIFF),
  });
}

export function paulsenZermeloExpectation(strength1, strength2) {
  if (![strength1, strength2].every(value => Number.isFinite(value) && value > 0 && value < 1)) {
    throw new Error("Both strengths must be strictly between zero and one.");
  }
  if (strength1 === strength2) return { expectedScore1: 0.5, expectedScore2: 0.5 };
  if (strength2 === 0.5) return { expectedScore1: strength1, expectedScore2: 1 - strength1 };
  if (strength1 === 0.5) return { expectedScore1: 1 - strength2, expectedScore2: strength2 };
  const a = strength1 * (1 - strength2);
  const b = strength2 * (1 - strength1);
  // Compute the smaller side directly and the other as its complement. This
  // evaluates the documented formula while preserving exact swap symmetry.
  if (a < b) {
    const expectedScore1 = a / (a + b);
    return { expectedScore1, expectedScore2: 1 - expectedScore1 };
  }
  const expectedScore2 = b / (a + b);
  return { expectedScore1: 1 - expectedScore2, expectedScore2 };
}

export function tournamentStrengthExpectation(player1, player2) {
  if (player1?.scope !== BETTY_STRENGTH_SCOPE || player2?.scope !== BETTY_STRENGTH_SCOPE
      || player1.section !== player2.section || player1.referenceElo !== player2.referenceElo) {
    throw new Error("Strengths from different sections or reference anchors cannot be compared.");
  }
  return paulsenZermeloExpectation(player1.strength, player2.strength);
}


export const BETTY_MODEL_STRATEGY = "betty-strength";
export const BETTY_WHITE_ELO_BONUS = 14.1855755875;
export const MISSING_ELO_INITIALIZATION_LABEL = "Elo fehlt · initialisiert nach dem Durchschnitt der 20 niedrigsten gültigen Elo-Werte dieser Sektion";

export function initializeTournamentPlayer(rating, reference) {
  const usesReplacement = !isValidTournamentElo(rating);
  const effectiveRating = usesReplacement ? reference?.replacementElo : Number(rating);
  return { ...initializeBettyStrength(effectiveRating, reference), usesReplacement };
}

export function calculateBettyChessProbabilities({
  player1Elo, player2Elo, player1Color = "white", effectiveMinutes,
  player1DrawFactor, player2DrawFactor, parameters, strengthReference,
}) {
  const player1 = initializeTournamentPlayer(player1Elo, strengthReference);
  const player2 = initializeTournamentPlayer(player2Elo, strengthReference);
  const adjusted1 = initializeBettyStrength(player1.rating + (player1Color === "white" ? BETTY_WHITE_ELO_BONUS : 0), strengthReference);
  const adjusted2 = initializeBettyStrength(player2.rating + (player1Color === "black" ? BETTY_WHITE_ELO_BONUS : 0), strengthReference);
  const expectation = tournamentStrengthExpectation(player1, player2).expectedScore1;
  const adjustedExpectation = tournamentStrengthExpectation(adjusted1, adjusted2).expectedScore1;
  const equalStrengthDrawProbability = calculateEqualStrengthDrawProbability({
    averageElo: (player1.rating + player2.rating) / 2, effectiveMinutes,
    player1DrawFactor, player2DrawFactor, parameters,
  });
  const differenceFactor = 2 * Math.min(adjustedExpectation, 1 - adjustedExpectation);
  const draw = equalStrengthDrawProbability * differenceFactor;
  return {
    player1Win: adjustedExpectation - draw / 2, draw,
    player2Win: 1 - adjustedExpectation - draw / 2,
    expectation, adjustedExpectation, equalStrengthDrawProbability, differenceFactor,
    effectiveEloDifference: player1.rating - player2.rating,
    modelStrategy: BETTY_MODEL_STRATEGY, strengthReference,
    player1Initialization: player1, player2Initialization: player2,
    whiteEloBonus: BETTY_WHITE_ELO_BONUS,
  };
}
